import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, isApiError, type KbCrawlStatus, type KbSourceRecord, type KbSourceSyncStatus } from '../lib/api'
import { useBidProfile } from '../lib/bid-profile'
import { KbSourceList } from './KbSourceList'
import { KbSourceForm } from './KbSourceForm'
import { ConfirmModal } from './ConfirmModal'
import { isRunLive } from './KbSourceSyncCell'

/** How often to re-read status WHILE something is actually indexing. Idle costs nothing. */
const POLL_MS = 5000

/**
 * Settings → Corporate knowledge sources. The screen that replaces hand-written SQL on the
 * production database (`BACKLOG.md:5`, and the mechanism behind `plans/IT_SCREENING.md` A-5.2,
 * which until now had nothing behind it).
 *
 * TENANT ADMINS only, and the gate is `isTenantAdmin` directly — never `isResolved`, which is
 * false whenever the bid is null and this page has no bid, so gating on it would hide the section
 * from everybody. The client check is an AFFORDANCE: the backend enforces the same rule, and
 * refuses every endpoint here before it queries anything (the listing reveals site URLs, drive ids
 * and folder layouts).
 *
 * Polling is CONDITIONAL. The settings page keeps every section mounted at once, so an
 * unconditional 5s interval would poll for as long as the page is open; instead it only runs while
 * a source reports a live, non-stale run. A run that dies leaves a stale heartbeat, which reads as
 * "interrupted" and stops the polling — rather than spinning forever against a process that is gone.
 *
 * There is deliberately no "index now" button: a full crawl is hours of work and real euros of
 * Azure embedding spend, so runs stay on the `kb-sync-corporate` job. This screen says what is
 * indexed and what happened; it does not start anything.
 */
export function KbSourcesSection() {
  const { t, lang } = useLang()
  const qc = useQueryClient()
  const { isTenantAdmin } = useBidProfile(null)
  const [editing, setEditing] = useState<KbSourceRecord | null | undefined>(undefined)
  const [deleting, setDeleting] = useState<KbSourceRecord | null>(null)
  const [error, setError] = useState('')

  const sourcesQ = useQuery({
    queryKey: ['kb-sources'],
    queryFn: () => api.kbSources(),
    enabled: isTenantAdmin,
  })

  const statusQ = useQuery({
    queryKey: ['kb-sync-status'],
    queryFn: () => api.kbSyncStatus(),
    enabled: isTenantAdmin,
    refetchInterval: (q) => (q.state.data?.sources.some(isRunLive) ? POLL_MS : false),
  })

  // The crawl lease (plans/KB_CRAWL_LEASE.md): not polled — the line is re-read on focus, and an
  // expiry already in the past renders as paused anyway.
  const crawlQ = useQuery({
    queryKey: ['kb-crawl-status'],
    queryFn: () => api.kbCrawlStatus(),
    enabled: isTenantAdmin,
  })

  if (!isTenantAdmin) return null

  const sources = sourcesQ.data?.sources ?? []
  const status = new Map<string, KbSourceSyncStatus>(
    (statusQ.data?.sources ?? []).map((s) => [s.sourceRef, s]),
  )

  /** This codebase does not use useMutation — plain handlers + invalidateQueries. */
  async function refresh(): Promise<void> {
    await qc.invalidateQueries({ queryKey: ['kb-sources'] })
    await qc.invalidateQueries({ queryKey: ['kb-sync-status'] })
  }

  async function confirmDelete(): Promise<void> {
    if (!deleting) return
    setError('')
    try {
      await api.deleteKbSource(deleting.id)
      setDeleting(null)
      await refresh()
    } catch (e) {
      setError(isApiError(e) ? e.serverMessage || e.message : String(e))
      setDeleting(null)
    }
  }

  // A 503 is a deployment fact ("no corporate KB on this server"), not a failure to report as one.
  const unconfigured = isApiError(sourcesQ.error) && sourcesQ.error.code === 'not_configured'

  return (
    <div className="settings-card kbsrc-card">
      <p>{t('kbsrc_intro')}</p>
      {crawlQ.data && (
        <p className="settings-note kbsrc-crawl" role="status">
          {crawlLine(crawlQ.data, lang, t('kbsrc_crawl_active'), t('kbsrc_crawl_paused'))}
        </p>
      )}
      {/* The action sits at the table's top-right corner, on its own line and off the table's
          top edge — `.settings-card-head` alone would pin it to the left, hard against the
          header row. */}
      <div className="settings-card-head kbsrc-head">
        <button type="button" className="mini ok" onClick={() => setEditing(null)}>
          {t('kbsrc_new')}
        </button>
      </div>

      {sourcesQ.isLoading ? (
        <p className="settings-note">{t('kbsrc_loading')}</p>
      ) : unconfigured ? (
        <p className="settings-note">{t('kbsrc_unconfigured')}</p>
      ) : sourcesQ.isError ? (
        <p className="settings-note" role="alert">
          {t('kbsrc_error')}
        </p>
      ) : sources.length === 0 ? (
        <p className="settings-note">{t('kbsrc_empty')}</p>
      ) : (
        <KbSourceList
          sources={sources}
          status={status}
          onEdit={setEditing}
          onDelete={setDeleting}
        />
      )}

      {error && (
        <p className="settings-note kbsrc-error" role="alert">
          {error}
        </p>
      )}

      {editing !== undefined && (
        <KbSourceForm
          source={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined)
            void refresh()
          }}
          onChanged={() => void refresh()}
        />
      )}

      {deleting && (
        <ConfirmModal
          title={t('kbsrc_delete_title')}
          message={t('kbsrc_delete_msg').replace('{name}', deleting.name)}
          danger
          onConfirm={() => void confirmDelete()}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  )
}

/** "Crawl: active as X until hh:mm", or paused when there is no lease or it has already run out. */
export function crawlLine(status: KbCrawlStatus, lang: string, active: string, paused: string): string {
  const lease = status.lease
  const until = lease ? new Date(lease.expiresAt) : null
  if (!lease || !until || Number.isNaN(until.getTime()) || until.getTime() <= Date.now()) return paused
  const time = until.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })
  return active.replace('{name}', lease.holder).replace('{time}', time)
}
